import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { DateTimeInput } from '../ui/DateTimeInput';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import { RichTextEditor } from '../ui/RichTextEditor';
import { formatRelativeToNow, toDateTimeLocalValue } from '@/utils/datetime';
import LocationPicker from '../maps/LocationPicker';
import CustomFieldsEditor, { type CustomField } from './CustomFieldsEditor';
import CommunicationPlanEditor, {
  communicationPlanSchema,
  validateCommunicationPlan,
  type CommunicationPlan,
} from './CommunicationPlanEditor';
import VenueCombobox, { type VenueSelection } from './VenueCombobox';
import AgentMultiSelect, { type AgentSelection } from './AgentMultiSelect';
import {
  CalendarIcon,
  HeartIcon,
  SignalIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const activitySchema = z.object({
  protectedPersonId: z.string().min(1, 'At least one protected person is required'),
  protectedPersonIds: z.array(z.string()).optional(), // Additional protected persons
  agentIds: z.array(z.string()).optional(), // Assigned security agents
  leadAgentId: z.string().nullable().optional(),
  activityType: z.enum(['TRAVEL', 'DINING', 'EVENT', 'MEETING', 'OTHER']),
  title: z.string().min(1, 'Title is required').max(200, 'Title too long'),
  description: z.string().optional(),
  startDatetime: z.string().min(1, 'Start date and time is required'),
  endDatetime: z.string().optional(),
  location: z.string().optional(),
  locationLatitude: z.number().optional().nullable(),
  locationLongitude: z.number().optional().nullable(),
  venueId: z.string().optional(),
  newVenueName: z.string().max(200).optional(),
  newVenueAddress: z.string().max(500).optional(),
  riskAssessment: z.string().optional(),
  securityRequirements: z.string().optional(),
  // Medical information fields
  closestHospitalName: z.string().max(200).optional(),
  closestHospitalAddress: z.string().max(500).optional(),
  closestHospitalPhone: z.string().max(50).optional(),
  closestHospitalDistance: z.string().max(100).optional(),
  closestHospitalTravelTime: z.string().max(100).optional(),
  closestHospitalCapabilities: z.string().max(500).optional(),
  alternativeMedicalFacilities: z.string().optional(),
  evacuationRoute: z.string().optional(),
  onsiteMedicalSupport: z.string().optional(),
  customFields: z.array(z.object({
    name: z.string(),
    value: z.string(),
    type: z.enum(['text', 'number', 'date', 'select', 'textarea']),
  })).optional(),
  communicationPlan: communicationPlanSchema.nullable().optional(),
  // Set after the user explicitly confirms creating an already-ended activity
  allowPastEnd: z.boolean().optional(),
}).superRefine((data, ctx) => {
  if (data.newVenueName && !data.newVenueAddress?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['newVenueAddress'],
      message: 'Address is required when adding a new venue',
    });
  }
  if (data.startDatetime && data.endDatetime &&
      new Date(data.endDatetime) <= new Date(data.startDatetime)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endDatetime'],
      message: 'End date/time must be after start date/time',
    });
  }
});

type ActivityFormData = z.infer<typeof activitySchema>;

interface ActivityFormProps {
  initialData?: Partial<ActivityFormData>;
  onSubmit: (data: ActivityFormData) => void | Promise<void>;
  onCancel: () => void;
  protectedPersons: Array<{ id: string; firstName: string; lastName: string; title: string }>;
  venues: Array<{ id: string; name: string; address: string; securityContactName?: string; securityContactPhone?: string }>;
  isLoading?: boolean;
  googleMapsApiKey?: string;
}

const MissionForm: React.FC<ActivityFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  protectedPersons,
  venues,
  isLoading = false,
  googleMapsApiKey = '',
}) => {
  const [customFields, setCustomFields] = useState<CustomField[]>(
    initialData?.customFields ?? []
  );
  const [communicationPlan, setCommunicationPlan] = useState<CommunicationPlan | null>(
    initialData?.communicationPlan ?? null
  );
  const [commsPlanError, setCommsPlanError] = useState<string | null>(null);
  const commsPlanSectionRef = useRef<HTMLDivElement>(null);
  const [activityType, setActivityType] = useState<string>(
    initialData?.activityType ?? 'MEETING'
  );
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>(
    initialData?.protectedPersonId ? [initialData.protectedPersonId] : []
  );
  const [agentSelection, setAgentSelection] = useState<AgentSelection>({
    agentIds: initialData?.agentIds ?? [],
    leadAgentId: initialData?.leadAgentId ?? null,
  });
  const [venueSelection, setVenueSelection] = useState<VenueSelection>(() => {
    const initialVenue = initialData?.venueId
      ? venues.find((venue) => venue.id === initialData.venueId)
      : undefined;
    return initialVenue ? { kind: 'existing', venue: initialVenue } : null;
  });

  const [pendingPastEndData, setPendingPastEndData] = useState<ActivityFormData | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ActivityFormData>({
    resolver: zodResolver(activitySchema as unknown as Parameters<typeof zodResolver>[0]),
    defaultValues: {
      ...initialData,
      startDatetime: initialData?.startDatetime ?? toDateTimeLocalValue(new Date()),
    },
  });

  const watchedActivityType = watch('activityType');
  const watchedStart = watch('startDatetime');
  const watchedEnd = watch('endDatetime');

  useEffect(() => {
    setActivityType(watchedActivityType);
  }, [watchedActivityType]);

  // An activity whose end (or start, when no end is set) is in the past would be
  // auto-completed and locked the moment it is created (VIS-136)
  const endsInPast = (data: { startDatetime?: string; endDatetime?: string }): boolean => {
    const now = new Date();
    if (data.endDatetime) {
      return new Date(data.endDatetime) < now;
    }
    return !!data.startDatetime && new Date(data.startDatetime) < now;
  };

  const onFormSubmit = (data: ActivityFormData) => {
    // Use the first selected person as the primary protected person
    const primaryPersonId = selectedPersonIds[0];

    if (communicationPlan) {
      const planError = validateCommunicationPlan(communicationPlan);
      if (planError) {
        setCommsPlanError(planError);
        commsPlanSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    setCommsPlanError(null);

    const payload: ActivityFormData = {
      ...data,
      protectedPersonId: primaryPersonId,
      protectedPersonIds: selectedPersonIds, // Send all person IDs including primary
      agentIds: agentSelection.agentIds,
      leadAgentId: agentSelection.leadAgentId,
      customFields,
      communicationPlan,
    };

    if (endsInPast(payload) && !payload.allowPastEnd) {
      setPendingPastEndData(payload);
      return;
    }

    onSubmit(payload);
  };

  const handleVenueChange = (selection: VenueSelection) => {
    setVenueSelection(selection);
    if (selection?.kind === 'existing') {
      setValue('venueId', selection.venue.id);
      setValue('newVenueName', undefined);
      setValue('newVenueAddress', undefined);
    } else if (selection?.kind === 'new') {
      setValue('venueId', undefined);
      setValue('newVenueName', selection.name);
    } else {
      setValue('venueId', undefined);
      setValue('newVenueName', undefined);
      setValue('newVenueAddress', undefined);
    }
  };

  const selectedVenue =
    venueSelection?.kind === 'existing'
      ? venues.find((venue) => venue.id === venueSelection.venue.id)
      : undefined;
  const venueSecurityContact =
    selectedVenue?.securityContactName && selectedVenue?.securityContactPhone
      ? { name: selectedVenue.securityContactName, phone: selectedVenue.securityContactPhone }
      : null;

  const handlePersonSelect = (personId: string) => {
    if (!selectedPersonIds.includes(personId)) {
      const newSelection = [...selectedPersonIds, personId];
      setSelectedPersonIds(newSelection);
      setValue('protectedPersonId', newSelection[0]); // Set primary person
    }
  };

  const handlePersonRemove = (personId: string) => {
    const newSelection = selectedPersonIds.filter(id => id !== personId);
    setSelectedPersonIds(newSelection);
    if (newSelection.length > 0) {
      setValue('protectedPersonId', newSelection[0]);
    } else {
      setValue('protectedPersonId', '');
    }
  };

  const movePrimaryPerson = (personId: string, direction: 'up' | 'down') => {
    const currentIndex = selectedPersonIds.indexOf(personId);
    if (currentIndex === -1) {return;}

    const newSelection = [...selectedPersonIds];
    if (direction === 'up' && currentIndex > 0) {
      [newSelection[currentIndex], newSelection[currentIndex - 1]] = [newSelection[currentIndex - 1], newSelection[currentIndex]];
    } else if (direction === 'down' && currentIndex < newSelection.length - 1) {
      [newSelection[currentIndex], newSelection[currentIndex + 1]] = [newSelection[currentIndex + 1], newSelection[currentIndex]];
    }

    setSelectedPersonIds(newSelection);
    setValue('protectedPersonId', newSelection[0]); // Update primary person
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <CalendarIcon className="w-5 h-5 mr-2" />
          {initialData ? 'Edit Activity' : 'Create New Activity'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 gap-4">
            {/* Protected Persons Multi-Select */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Protected Persons * <span className="text-xs text-gray-500">(Select one or more)</span>
              </label>

              {/* Selected persons display */}
              {selectedPersonIds.length > 0 && (
                <div className="mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="space-y-2">
                    {selectedPersonIds.map((personId, index) => {
                      const person = protectedPersons.find(p => p.id === personId);
                      if (!person) {return null;}
                      const isPrimary = index === 0;

                      return (
                        <div key={personId} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center space-x-2">
                            {isPrimary && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded">
                                PRIMARY
                              </span>
                            )}
                            <span className="font-medium">
                              {person.firstName} {person.lastName}
                            </span>
                            <span className="text-sm text-gray-500">- {person.title}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => movePrimaryPerson(personId, 'up')}
                                className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                title="Move up"
                              >
                                ↑
                              </button>
                            )}
                            {index < selectedPersonIds.length - 1 && (
                              <button
                                type="button"
                                onClick={() => movePrimaryPerson(personId, 'down')}
                                className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                title="Move down"
                              >
                                ↓
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handlePersonRemove(personId)}
                              className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-400"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Dropdown to add more persons */}
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    handlePersonSelect(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
              >
                <option value="">+ Add protected person...</option>
                {protectedPersons
                  .filter(p => !selectedPersonIds.includes(p.id))
                  .map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.firstName} {person.lastName} - {person.title}
                    </option>
                  ))}
              </select>

              {/* Hidden input for react-hook-form validation */}
              <input type="hidden" {...register('protectedPersonId')} value={selectedPersonIds[0] ?? ''} />
              {errors.protectedPersonId && selectedPersonIds.length === 0 && (
                <p className="text-red-500 text-sm mt-1">{errors.protectedPersonId.message}</p>
              )}
              {selectedPersonIds.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedPersonIds.length} person{selectedPersonIds.length > 1 ? 's' : ''} selected.
                  The first person is the primary protected person.
                </p>
              )}
            </div>

            {/* Security Detail (assigned agents) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Security Detail <span className="text-xs text-gray-500">(Assign security agents)</span>
              </label>
              <AgentMultiSelect
                value={agentSelection}
                onChange={setAgentSelection}
                startDatetime={watch('startDatetime')}
                endDatetime={watch('endDatetime')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Activity Type *
              </label>
              <select
                {...register('activityType')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
              >
                <option value="MEETING">Meeting</option>
                <option value="TRAVEL">Travel</option>
                <option value="DINING">Dining</option>
                <option value="EVENT">Event</option>
                <option value="OTHER">Other</option>
              </select>
              {errors.activityType && (
                <p className="text-red-500 text-sm mt-1">{errors.activityType.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Title *
            </label>
            <input
              type="text"
              {...register('title')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
              placeholder="Enter activity title"
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
            )}
          </div>

          <RichTextEditor
            label="Description"
            value={watch('description') ?? ''}
            onChange={(html) => setValue('description', html)}
            placeholder="Enter activity description"
            minHeight={120}
          />

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <DateTimeInput
                label="Start Date & Time"
                required
                value={watchedStart ?? ''}
                onChange={(value) => setValue('startDatetime', value, { shouldValidate: true })}
                error={errors.startDatetime?.message}
              />
              {watchedStart && !errors.startDatetime && (
                <p className="text-xs text-gray-500 mt-1">
                  starts {formatRelativeToNow(watchedStart)}
                </p>
              )}
            </div>

            <div>
              <DateTimeInput
                label="End Date & Time"
                value={watchedEnd ?? ''}
                onChange={(value) => setValue('endDatetime', value, { shouldValidate: true })}
                error={errors.endDatetime?.message}
              />
              {watchedEnd && !errors.endDatetime && (
                new Date(watchedEnd) < new Date() ? (
                  <p className="text-xs text-warning-600 dark:text-warning-400 mt-1">
                    ⚠ already ended ({formatRelativeToNow(watchedEnd)}) — it will be created as completed
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    ends {formatRelativeToNow(watchedEnd)}
                  </p>
                )
              )}
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <LocationPicker
              apiKey={googleMapsApiKey}
              label="Location"
              value={{
                location: watch('location'),
                latitude: watch('locationLatitude') ?? undefined,
                longitude: watch('locationLongitude') ?? undefined,
              }}
              onChange={({ location, latitude, longitude }) => {
                setValue('location', location);
                setValue('locationLatitude', latitude);
                setValue('locationLongitude', longitude);
              }}
              placeholder="Search for a location or click on the map"
              mapHeight="250px"
            />

            <div>
              <label className="block text-sm font-medium mb-2">
                Venue (Optional)
              </label>
              <VenueCombobox
                venues={venues}
                value={venueSelection}
                onChange={handleVenueChange}
              />
              <p className="text-xs text-gray-500 mt-1">
                Select an existing venue, or type a name to add a new one.
              </p>
              {venueSelection?.kind === 'new' && (
                <div className="mt-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <p className="text-sm font-medium mb-2">
                    New venue: <span className="text-primary-600 dark:text-primary-400">{venueSelection.name}</span>
                  </p>
                  <label className="block text-sm font-medium mb-2">
                    Venue Address *
                  </label>
                  <input
                    type="text"
                    {...register('newVenueAddress')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                    placeholder="e.g., 123 Main Street, City, State 12345"
                  />
                  {errors.newVenueAddress && (
                    <p className="text-red-500 text-sm mt-1">{errors.newVenueAddress.message}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Security Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RichTextEditor
              label="Risk Assessment"
              value={watch('riskAssessment') ?? ''}
              onChange={(html) => setValue('riskAssessment', html)}
              placeholder="Describe potential risks and mitigation strategies"
              minHeight={120}
            />

            <RichTextEditor
              label="Security Requirements"
              value={watch('securityRequirements') ?? ''}
              onChange={(html) => setValue('securityRequirements', html)}
              placeholder="Describe security requirements and personnel needed"
              minHeight={120}
            />
          </div>

          {/* Communication Plan */}
          <div className="border-t pt-6" ref={commsPlanSectionRef}>
            <h4 className="text-lg font-medium mb-4 flex items-center">
              <SignalIcon className="w-5 h-5 mr-2 text-primary-500" />
              Communication Plan <span className="ml-2 text-sm font-normal text-gray-500">(Optional)</span>
            </h4>
            <CommunicationPlanEditor
              value={communicationPlan}
              onChange={(plan) => {
                setCommunicationPlan(plan);
                setCommsPlanError(null);
              }}
              venueSecurityContact={venueSecurityContact}
              error={commsPlanError}
            />
          </div>

          {/* Medical Information */}
          <div className="border-t pt-6">
            <h4 className="text-lg font-medium mb-4 flex items-center">
              <HeartIcon className="w-5 h-5 mr-2 text-red-500" />
              Medical Information <span className="ml-2 text-sm font-normal text-gray-500">(Optional)</span>
            </h4>

            {/* Closest Hospital Information */}
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h5 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">Closest Hospital</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Hospital Name</label>
                    <input
                      type="text"
                      {...register('closestHospitalName')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="e.g., City General Hospital"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Hospital Phone</label>
                    <input
                      type="tel"
                      {...register('closestHospitalPhone')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="e.g., (555) 123-4567"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Hospital Address</label>
                    <input
                      type="text"
                      {...register('closestHospitalAddress')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="e.g., 123 Main Street, City, State 12345"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Distance from Location</label>
                    <input
                      type="text"
                      {...register('closestHospitalDistance')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="e.g., 2.5 miles"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Estimated Travel Time</label>
                    <input
                      type="text"
                      {...register('closestHospitalTravelTime')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="e.g., 8 minutes"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Hospital Capabilities</label>
                    <input
                      type="text"
                      {...register('closestHospitalCapabilities')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="e.g., Level I Trauma Center, 24/7 Emergency Room"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Planning */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h5 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">Emergency Planning</h5>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Alternative Medical Facilities</label>
                    <textarea
                      {...register('alternativeMedicalFacilities')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="List backup hospitals, urgent care centers, or clinics nearby"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Evacuation Route</label>
                    <textarea
                      {...register('evacuationRoute')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="Describe the planned route to the hospital in case of emergency"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">On-Site Medical Support</label>
                    <textarea
                      {...register('onsiteMedicalSupport')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="Describe any medical personnel, equipment, or resources available at the location"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Fields */}
          <CustomFieldsEditor
            activityType={activityType}
            customFields={customFields}
            onChange={setCustomFields}
          />

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : (initialData ? 'Update Activity' : 'Create Activity')}
            </Button>
          </div>
        </form>

        {/* Explicit confirmation before creating an already-ended (instantly
            locked) activity — VIS-136 */}
        <ConfirmationDialog
          isOpen={pendingPastEndData !== null}
          onClose={() => setPendingPastEndData(null)}
          onConfirm={() => {
            if (pendingPastEndData) {
              onSubmit({ ...pendingPastEndData, allowPastEnd: true });
            }
            setPendingPastEndData(null);
          }}
          title="Activity has already ended"
          message="The end time you entered is in the past, so this activity will immediately be marked COMPLETED and locked for editing. Do you want to continue?"
          confirmText="Create as Completed"
          cancelText="Go Back"
          variant="warning"
          isLoading={isLoading}
        />
      </CardContent>
    </Card>
  );
};

export default MissionForm;
export type { ActivityFormData };